import { apiRequest } from '../../lib/api'
import type {
  ActivityLogsEnvelope,
  BusinessIacrmSettingsEnvelope,
  BusinessIacrmTestEnvelope,
  SettingsEnvelope,
} from '../../types/settings'
import type { SyncJobSummary, SyncOverviewEnvelope } from '../../types/sync'

export async function fetchSettings() {
  return apiRequest<SettingsEnvelope>('/v1/settings')
}

export async function updateOwnSettings(payload: {
  display_name: string
  email: string
  phone_number?: string | null
  avatar_url?: string | null
}) {
  return apiRequest<SettingsEnvelope>('/v1/settings/own', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function uploadOwnAvatar(file: File) {
  const body = new FormData()
  body.append('avatar', file)

  return apiRequest<SettingsEnvelope>('/v1/settings/own/avatar', {
    method: 'POST',
    body,
  })
}

export async function requestOwnEmailChange(payload: { email: string }) {
  return apiRequest<SettingsEnvelope>('/v1/settings/own/email/request-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function resendOwnEmailVerification() {
  return apiRequest<{ data: { message: string } }>('/v1/settings/own/email/resend-verification', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
}

export async function verifyOwnEmailCode(payload: { code: string }) {
  return apiRequest<SettingsEnvelope>('/v1/settings/own/email/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function fetchOwnActivityLogs() {
  return apiRequest<ActivityLogsEnvelope>('/v1/settings/own/activity-logs')
}

export async function requestOwnPasswordChange(payload: {
  current_password: string
  password: string
  password_confirmation: string
}) {
  return apiRequest<{ data: { message: string } }>('/v1/settings/own/password/request-change', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function updateOwnPassword(payload: {
  current_password: string
  password: string
  password_confirmation: string
  verification_code: string
}) {
  return apiRequest<{ data: { message: string } }>('/v1/settings/own/password', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function updateBusinessSettings(payload: {
  display_name: string
  contact_email?: string | null
  contact_phone?: string | null
  website_url?: string | null
  timezone?: string | null
}) {
  return apiRequest<SettingsEnvelope>('/v1/settings/business', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function uploadBusinessLogo(file: File) {
  const body = new FormData()
  body.append('logo', file)

  return apiRequest<SettingsEnvelope>('/v1/settings/business/logo', {
    method: 'POST',
    body,
  })
}

export async function fetchBusinessIacrmSettings() {
  return apiRequest<BusinessIacrmSettingsEnvelope>('/v1/settings/business/iacrm')
}

export async function updateBusinessIacrmSettings(payload: {
  base_url: string
  api_key?: string | null
  auto_sync_enabled: boolean
}) {
  return apiRequest<BusinessIacrmSettingsEnvelope>('/v1/settings/business/iacrm', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function testBusinessIacrmConnection(payload: {
  base_url: string
  api_key: string
  auto_sync_enabled: boolean
}) {
  return apiRequest<BusinessIacrmTestEnvelope>('/v1/settings/business/iacrm/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function fetchOwnIacrmSettings() {
  return apiRequest<BusinessIacrmSettingsEnvelope>('/v1/settings/own/iacrm')
}

export async function updateOwnIacrmSettings(payload: {
  base_url: string
  api_key?: string | null
  auto_sync_enabled: boolean
}) {
  return apiRequest<BusinessIacrmSettingsEnvelope>('/v1/settings/own/iacrm', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function testOwnIacrmConnection(payload: {
  base_url: string
  api_key: string
  auto_sync_enabled: boolean
}) {
  return apiRequest<BusinessIacrmTestEnvelope>('/v1/settings/own/iacrm/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

export async function fetchSyncOverview() {
  return apiRequest<SyncOverviewEnvelope>('/v1/sync/overview')
}

export async function fetchSyncJobs(params?: { status?: string; job_type?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.job_type) searchParams.set('job_type', params.job_type)
  const query = searchParams.toString()
  return apiRequest<{ data: SyncJobSummary[]; meta: { total: number } }>(`/v1/sync/jobs${query ? `?${query}` : ''}`)
}
